import React from 'react';
import FloatingMenu from '../FloatingMenu';
import { Box, Paper, Typography, Grid, ListItemIcon, Container } from '@mui/material';
import { motion } from 'framer-motion';
import '../styles/PageContent.css';

// Import NEW specific icons
import IconIntro from '../../assets/images/icon-intro.svg';
import IconWaste from '../../assets/images/icon-waste.svg';
import IconDiet from '../../assets/images/icon-diet.svg';
import IconTime from '../../assets/images/icon-time.svg';
import IconTools from '../../assets/images/icon-tools.svg';

// Define styles for icons
const iconStyle = {
    filter: 'invert(20%) sepia(51%) saturate(5000%) hue-rotate(280deg) brightness(95%) contrast(90%)',
    width: '24px',
    height: '24px',
    marginRight: '16px',
};

// Use CSS classes instead of inline styles for better responsiveness
const problemPoints = [
  {
    icon: IconIntro,
    title: 'The Daily Struggle',
    description: 'Many home cooks face daily hurdles with food waste 🗑️, mealtime stress 🤯, and making the most of their pantry. Finding the right recipe—one that matches available ingredients, dietary needs, and time constraints—is a common struggle 😩. Traditional tools often fall short, leaving users juggling multiple sources and lacking true personalization.',
    alt: 'Introduction icon',
    imageName: 'intro.png',
  },
  {
    icon: IconWaste,
    title: 'Food Waste',
    description: 'Significant amounts of food are wasted because ingredients are forgotten, spoil, or cooks aren\'t sure how to use them up effectively.',
    alt: 'Waste icon',
    imageName: 'waste_1.png',
  },
  {
    icon: IconDiet,
    title: 'Dietary & Preference Mismatches',
    description: 'Finding appealing recipes that strictly adhere to dietary restrictions (allergies, health goals) or personal preferences can be frustrating and time-consuming.',
    alt: 'Dietary restrictions icon',
    imageName: 'diet.png',
  },
  {
    icon: IconTime,
    title: 'Meal Planning Overwhelm',
    description: 'The daily grind of deciding what to cook, planning meals, and finding inspiration often leads to stress and reliance on the same few repetitive dishes.',
    alt: 'Time icon',
    imageName: 'time.png',
  },
  {
    icon: IconTools,
    title: 'Fragmented Kitchen Tools',
    description: 'Home cooks often jump between recipe sites, nutrition calculators, and inventory apps, lacking a single, integrated platform for managing their kitchen.',
    alt: 'Fragmented tools icon',
    imageName: 'tools.png',
  },
];

// Animation variants for the container to stagger children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3
    }
  },
};

// Animation variants for each item
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  },
};

const Problem = () => (
  <Box className="page-container" sx={{ py: { xs: 4, md: 6 }, background: '#f0f0f0' }}>
    <FloatingMenu />
    <Container maxWidth="lg">
      {/* Title Box - Remains separate */}
      <Paper
        elevation={2}
        sx={{
          padding: { xs: 2, md: 3 },
          borderRadius: '12px',
          background: '#e0c3fc',
          mb: 6,
          textAlign: 'center',
        }}
      >
        <Typography variant="h3" component="h1" className="section-title" sx={{ color: '#3a0ca3', fontWeight: 'bold' }}>
          The Kitchen Challenge 🤔
        </Typography>
      </Paper>

      {/* Grid container wrapped with motion.div */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Grid container spacing={4} justifyContent="center">
          {problemPoints.map((point, index) => (
            <Grid item xs={12} sm={6} md={5} key={index} component={motion.div} variants={itemVariants}>
              <Paper
                elevation={3}
                className="point-box"
                sx={{
                  background: '#F8E8EE',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Title and Icon Row */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ListItemIcon sx={{ minWidth: 'auto' }}>
                    <img src={point.icon} alt={point.alt} style={iconStyle} />
                  </ListItemIcon>
                  <Typography variant="h5" component="h3" sx={{ color: '#3a0ca3', fontWeight: 500 }}>
                    {point.title}
                  </Typography>
                </Box>

                {/* Image Container */}
                <Box className="image-container">
                  <img
                    className="responsive-image"
                    src={require(`../../assets/images/problem/${point.imageName}`)}
                    alt={`${point.title} illustration`}
                    onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span>Image not found</span>'; }}
                  />
                </Box>

                {/* Description Text */}
                <Typography variant="body1" sx={{ color: '#444' }}>
                  {point.description.replace(/<em>(.*?)<\/em>/g, '$1')}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </motion.div>
    </Container>
  </Box>
);

export default Problem;
